<template>
    <div class="lw-chart"
        :ref="el => chartDOM_r = el" 
    >
        <!-- 在此插入图表 -->
    </div>
</template>

<script setup lang="ts">
import { createChart } from 'lightweight-charts';
import useGlobalStore from '@/stores/global';
import type {
    SeriesOptions,
    ChartOptions,
    TikcArr,
} from '../ts/wavesData'; 
import {
    oChartConfig01_tt,
} from '../ts/wavesData'; 


const props = defineProps({
    type: String, // 图表类型，示例： Area, Candlestick, 
    selectedPair: String, // 示例： FuryWaves_USDT 
    // --- ↑ 字符型内容 -----------------------------------------  
    markers: Array, // 每日0点的秒级时间戳 
    data: { // 核心数据 
        type: Object as PropType<TikcArr>,
        default: () => ([]),
    },
    seriesOptions: {
        type: Object as PropType<SeriesOptions>,
        default: () => ({}),
    },
    chartOptions: {
        type: Object as PropType<ChartOptions>,
        default: () => ({}),
    },
    timeScaleOptions: null,
    priceScaleOptions: null,
    rangeChangeHandler: {
        type: Function,
    },
    autosize: { // 似乎没用到  
        type: Boolean,
    },
});

const {t: $tFn} = useNuxtApp().$i18n;
let chartDOM_r = ref(null);
let chartIns_h = null; // 图表实例 
let oChartSeries_e = null; // 图表实例下的方法库: addAreaSeries() | addCandlestickSeries()
// -- ↑ 图表库 -----------------------------------
const globalStore = useGlobalStore();
const iWidth = computed(() => globalStore.innerWidth);


// ↓ 执行于屏幕宽度变化，且 props.autoSize 为 true 时
const executeResize = () => { 
    if (!chartIns_h || !chartDOM_r.value) return;
    const oInfo = chartDOM_r.value.getBoundingClientRect();
    chartIns_h.resize(
        oInfo.width,
        oInfo.height,
    );
}


// 字符拼接，返回： addAreaSeries() | addCandlestickSeries() 
const getSeriesName_l = (sType:string) => {
    return `add${sType}Series`;
};


// ↓ 设定图表显示当前时刻（ 在 mounted() 内部有调用）  
// ↓ 延时调用 scrollToRealTime()
const fnScrollToRealTime_u = (iDelay = 1_000) => {
    if (!chartIns_h) return;
    setTimeout(()=>{
        if (!chartIns_h) return;
        // ↓ 实测调用到滚动到最右侧 
        chartIns_h.timeScale().scrollToRealTime();
    }, iDelay);
};



// 启动方法：mounted 和 resize 事件后的执行动作 
const fnAddOneSeries = (oProps) => {
    if (!chartIns_h) return;
    // ↓ 取得2个方法名： addAreaSeries() | addCandlestickSeries()
    const sSeriesName = getSeriesName_l(oProps.type); 
    // console.log("设定图表方法名：", sSeriesName);
    // console.log("设定图表配置荐：", deepCopy(oProps.seriesOptions)); 
    oChartSeries_e = chartIns_h[sSeriesName](
        oProps.seriesOptions, 
    );
    if (oChartSeries_e){
        oChartSeries_e.setData(oProps.data);
    }
};


// ↓ 监听屏幕宽度变化，执行重绘 
watch(
    () => [props.autosize, iWidth.value],
    (oNew) => {
        if (!oNew[0] || !oNew[1])
            return;
        executeResize();
    },
);

// ↓ 移植到此，暂未进入实用阶段 
// ↓ 在原版中被不停地触发：以修改 seriesOptions.priceLineColor 
watch(
    () => props.seriesOptions,
    (oNewSeriesOptions) => {
        if (!oChartSeries_e) return;
        // ↓ 主要是修改横线的颜色 
        oChartSeries_e.applyOptions(oNewSeriesOptions);
    },
);


// ↓ 在此 watch 重点数据，并刷新 
// ↓ 在 tick 模式下 50 毫秒后调用一次，即每秒 20 次
// ↓ 接收数据流，刷新图表 
watch(
    () => props.data,
    // 旧参数名： d, p 
    (aNewData, aOldData) => {
        if (!oChartSeries_e) return;
        // console.log('watch 被数据触发，开始渲染图表：', deepCopy(aNewData)); 
        // aNewData 在切换图表粒度时长度为 0 （原版的表现） 
        const oFirstNew = aNewData?.[0];
        const oFirstOld = aOldData?.[0];
        // ↓ 所有条件都满足，即执行追加 
        const toUpdate = (
            aOldData
            && oFirstNew?.time
            // ↓ 此行及以下都在用首元素做判断，以证实当前数组左侧没注入新数据 
            && (oFirstNew?.time === oFirstOld?.time)
            // ↓ tick 模式专用，在柱图模式下，2值都是空仍然相等，不会阻碍判断通过 
            && (oFirstNew?.value === oFirstOld?.value)
        );
        if (0 && import.meta.dev){
            console.log('绘制图表：💕', toUpdate ? '追加一项 >>>' : '从头绘制 😎😎😎😎');  
        }
        // ↓ 按情况执行从0绘制，或中途追加 
        // ↓ 追加，初步判断：将收到的动态数据绘制到图表上  
        if (toUpdate){ // 追加
            const oLast = aNewData.at(-1);
            // console.log("最后一项：", JSON.stringify(oLast)); 
            oChartSeries_e.update(oLast);
        }else if (aNewData?.length){ // 从头绘制
            oChartSeries_e.setData(aNewData);
            const oScale = chartIns_h.timeScale();
            const oRange = oScale.getVisibleLogicalRange() || {};
            console.log('重绘后的位置信息：', JSON.stringify(oRange));  
            if (oRange.from < 10){
                // console.log("触发二次加载, oRange.from=", oRange.from);
                Sleep(100).then(() => {
                    props.rangeChangeHandler(oRange, 2);
                });
            }
        }
        // ↓ 临时关设定 0 点标记 
        checkAndSetZeroClock();
        // console.log("chartIns_h.getVisibleRange()", chartIns_h.timeScale()?.getVisibleRange());
        // chartIns_h.timeScale()?.scrollToRealTime();
    },
    { immediate: true, deep: true },
);

// ↓ 监听图表类型变化 
watch(
    () => props.type, // Candlestick | Area （后者是 tick 模式专用）
    (sChartTypeNew) => {
        console.log('图表类型更改：', sChartTypeNew); 
        const toDo = (oChartSeries_e && chartIns_h);
        if (!toDo) return;
        oChartSeries_e.setMarkers([]);
        chartIns_h.removeSeries(oChartSeries_e); 
        fnAddOneSeries(props);
    }
);

// ↓ 监听图表类型变化 
// ↓ 移植到此，暂未被触发  
watch(
    () => props.chartOptions, 
    (oNewChartOptions) => {
        if (chartIns_h && oNewChartOptions){
            chartIns_h.applyOptions(oNewChartOptions);
            chartIns_h.timeScale().fitContent();
        }
    }
);


// ★ 此处，在生命周期上方的最后一项监听是针对： props.markers 
// ★ 已经取消此项，保存此注释

// ↑ watch() 
// ============================================================================
// ↓ 生命周期方法

onMounted(() => {
    // console.log('图表组件 onMounted', chartDOM_r.value); 
    if (!chartDOM_r.value) return; // 无 dom 
    const {
        timeScaleOptions, // 原版此值一直为空
        priceScaleOptions, // 原版此值一直为空
        rangeChangeHandler,
    } = props;
    chartIns_h = createChart(
        chartDOM_r.value,
        props.chartOptions as any,
    );
    fnAddOneSeries(props);
    if (priceScaleOptions){ 
        chartIns_h.priceScale("right").applyOptions(
            priceScaleOptions,
        );
    }
    if (timeScaleOptions){
        chartIns_h.timeScale().applyOptions(
            timeScaleOptions,
        );
    }
    if (rangeChangeHandler){
        // ↓ 监听页面横向变化（实测纵向缩放无触发） 
        chartIns_h.timeScale().subscribeVisibleLogicalRangeChange(
            rangeChangeHandler
        )
    }
    fnScrollToRealTime_u();
    chartIns_h.timeScale().fitContent();
});


// ↓ 退场操作 
onBeforeUnmount(()=>{
    console.log("Waves 游戏开始退场 --------------- 💥");
    if (chartIns_h){
        chartIns_h.timeScale().unsubscribeVisibleLogicalRangeChange(
            props.rangeChangeHandler,
        );
        chartIns_h.remove();
    }
    chartIns_h = void 0;
    oChartSeries_e = void 0;
});


// === ↓ 最下方放置一些自制的方法 ==============================

const oLastSet = { 
    head: NaN,
    tail: NaN,
    length: NaN,
};

// ↓ 标记 0点 ❤️ 有意设为异步方法 
const checkAndSetZeroClock = async ()=>{
    if (!oChartSeries_e) return;
    const toSet = (
        props.markers.at(0) != oLastSet.head
        || props.markers.at(-1) != oLastSet.tail
        || props.markers.length != oLastSet.length
    );
    // console.log("绘制 0 点标记？", toSet);
    if (!toSet) return;
    Object.assign(oLastSet, {
        head: props.markers.at(0),
        tail: props.markers.at(-1),
        length: props.markers.length,
    });
    const isAreaMode = props.type === oChartConfig01_tt.area;
    const obj = {
        time: '', // 下方赋值
        id: '', // 下方赋值： `${C}:${L}`,
        position: isAreaMode ? "aboveBar" : "belowBar",
        color: "#2283f6",
        shape: "circle",
        text: $tFn('waves.ResetTo') + ' $1000', // '0点归位', // t("futures.tradingChart.markers.resetText"), 
        size: 2,
    };
    const aResult = props.markers.map((iCurTime, idx) => {
        return {
            ...obj,
            time: iCurTime,
            id: `${iCurTime}:${idx}`,
        };
    });
    oChartSeries_e.setMarkers(aResult);
}


</script>


<style scoped lang="scss">
.lw-chart{
    width: 100%;
    height: 100%; 
    min-height: 300px; 
}

</style>


